import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { dialogStore } from '../../stores/dialog.store.ts';

export default function CustomDialog() {
	const current = dialogStore((s) => s.current);
	const resolveAndClose = dialogStore((s) => s.resolveAndClose);

	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef(null);
	const confirmBtnRef = useRef(null);

	useEffect(() => {
		if (!current) return;
		setInputValue(current.defaultValue ?? '');
		const t = setTimeout(() => {
			if (current.kind === 'prompt') inputRef.current?.focus();
			else confirmBtnRef.current?.focus();
		}, 0);
		return () => clearTimeout(t);
	}, [current?.id]);

	useEffect(() => {
		if (!current) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => { document.body.style.overflow = prev; };
	}, [current?.id]);

	useEffect(() => {
		if (!current) return;
		const onKey = (e) => {
			if (e.key === 'Escape') handleCancel();
			else if (e.key === 'Enter' && current.kind !== 'prompt') {
				e.preventDefault();
				handleConfirm();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current?.id]);

	if (!current) return null;

	const handleConfirm = () => {
		if (current.kind === 'alert') resolveAndClose(undefined);
		else if (current.kind === 'confirm') resolveAndClose(true);
		else if (current.kind === 'prompt') resolveAndClose(inputValue);
	};

	const handleCancel = () => {
		if (current.kind === 'alert') resolveAndClose(undefined);
		else if (current.kind === 'confirm') resolveAndClose(false);
		else if (current.kind === 'prompt') resolveAndClose(null);
	};

	const isDestructive = current.destructive;
	const showCancel = current.kind !== 'alert';

	return (
		<>
			<div
				className="fixed inset-0 bg-black/50 z-[60] animate-fade-in"
				onClick={handleCancel}
				aria-hidden="true"
			/>
			<div
				className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none"
				role="dialog"
				aria-modal="true"
			>
				<div className="animate-pop-in bg-white w-full max-w-sm rounded-2xl shadow-2xl flex flex-col pointer-events-auto overflow-hidden">
					<div className="flex items-start justify-between px-5 pt-5">
						<div className="flex-1 min-w-0">
							{current.title && (
								<h2 className="text-base font-bold text-gray-900 mb-1 break-words">{current.title}</h2>
							)}
							<p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{current.message}</p>
						</div>
						<button
							type="button"
							onClick={handleCancel}
							className="ml-3 -mr-1 p-1 rounded-full hover:bg-gray-100 text-gray-400"
							aria-label="Close"
						>
							<X size={18} />
						</button>
					</div>

					{current.kind === 'prompt' && (
						<div className="px-5 pt-4">
							<textarea
								ref={inputRef}
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								placeholder={current.placeholder}
								rows={3}
								className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>
					)}

					<div className="flex gap-2 p-4 pt-5">
						{showCancel && (
							<button
								type="button"
								onClick={handleCancel}
								className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold"
							>
								{current.cancelLabel}
							</button>
						)}
						<button
							ref={confirmBtnRef}
							type="button"
							onClick={handleConfirm}
							className={[
								'flex-1 py-2.5 rounded-xl text-sm font-semibold text-white',
								isDestructive
									? 'bg-red-500 hover:bg-red-600'
									: 'bg-black hover:bg-gray-800',
							].join(' ')}
						>
							{current.confirmLabel}
						</button>
					</div>
				</div>
			</div>
		</>
	);
}
